"use client";

import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";

const caveat = Caveat({
  subsets: ["latin"],
  weight: ["700"],
  variable: "--font-caveat",
});

/* ─────────────────────────────────────────────────────────────
   1. PROPERTY MANAGEMENT: SEAL THE DEAL WITH LEADS & LISTINGS
   Centered title + two copy columns + Rental Contracts Kanban card
   ───────────────────────────────────────────────────────────── */
export function PropertySealTheDealSection({ section }) {
  const kanbanCols = [
    {
      title: "Park Station",
      count: 4,
      tenants: [
        { name: "My Company",  amount: "$ 1.15", so: "S00011", date: "09/20/2024 15:10:05" },
        { name: "OdooBot",     amount: "$ 1.15", so: "S00010", date: "09/20/2024 15:10:05" },
        { name: "OdooBot",     amount: "$ 1.15", so: "S0009",  date: "09/20/2024 15:10:05" },
        { name: "OdooBot",     amount: "$ 1.15", so: "S0008",  date: "09/20/2024 15:10:06" },
      ],
    },
    {
      title: "Immeuble Bellevue",
      count: 2,
      tenants: [
        { name: "James Doe",       amount: "$ 1.15", so: "S0007", date: "09/20/2024  15:10:05" },
        { name: "Joana Davis",     amount: "$ 1.15", so: "S0006", date: "09/20/2024  15:10:05" },
        { name: "Bertrand Dupuits",amount: "$ 1.15", so: "S00001",date: "09/20/2024 15:10:05" },
      ],
    },
    {
      title: "Ferme Saint-Jean",
      count: 5,
      tenants: [
        { name: "Denis Garnier",  amount: "$ 1.15", so: "S0005", date: "09/20/2024  15:10:05" },
        { name: "Nelly Curtis",   amount: "$ 1.15", so: "S0004", date: "09/20/2024  15:10:05" },
        { name: "Mila Warner",    amount: "$ 1.15", so: "S0003", date: "09/20/2024  15:10:05" },
        { name: "Lara Deschamps", amount: "$ 1.15", so: "S0002", date: "09/20/2024  15:10:05" },
      ],
    },
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-6 sm:px-12 lg:px-24 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto space-y-8">

        {/* CENTERED HEADER */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center space-y-6 w-full mx-auto"
        >
          {/* Caveat handwritten title with red underline on "leads and listings" */}
          <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
            Seal the deal with{" "}
            <span className="relative inline-block">
              leads and listings
              <svg className="absolute -bottom-1 left-0 w-full h-4 overflow-visible" viewBox="0 0 320 10" fill="none">
                <path d="M 2 6 Q 80 1, 160 6 T 318 6" stroke="#F87171" strokeWidth="5" strokeLinecap="round" fill="none" />
              </svg>
            </span>
          </h2>

          {/* Two-column bullet descriptions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 text-left w-full">
            <p className="text-gray-300 text-xl leading-relaxed">
              <strong className="text-white font-bold">Efficient lead management.</strong>{" "}
              Automatically generate leads from website forms or add them manually, ensuring every inquiry is tracked and followed up.
            </p>
            <p className="text-gray-300 text-xl leading-relaxed">
              <strong className="text-white font-bold">Comprehensive property listings.</strong>{" "}
              Manage all your properties with the Properties app. For easy reference, keep detailed records for each property, including descriptions, pricing, and availability.
            </p>
          </div>
        </motion.div>


        {/* ODOO PROPERTIES KANBAN CARD */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="w-full flex justify-center"
        >
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 text-slate-800 font-sans overflow-hidden w-full max-w-5xl">

            {/* Top Nav Bar */}
            <div className="flex flex-wrap items-center justify-between bg-white border-b border-slate-100 px-3 py-1.5 text-[7.5px] text-slate-500 gap-1">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 text-white text-[8px] font-bold flex-shrink-0">●</span>
                <span className="font-semibold text-slate-700">Properties</span>
                <span>Rental Contracts</span>
                <span>Availability</span>
                <span>Properties</span>
                <span>Configuration</span>
              </div>
              <div className="flex items-center gap-1 text-[7px] text-slate-400">
                <span>🔴</span><span>💬</span><span>⏱</span><span>✕</span>
                <span className="font-semibold text-slate-700 ml-1">My Company</span>
                <span className="inline-flex w-4 h-4 rounded-full bg-violet-500 items-center justify-center text-white text-[7px] font-bold">A</span>
              </div>
            </div>

            {/* Sub-nav: New + search + view toggles */}
            <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 px-3 py-1.5 text-[7.5px]">
              <span className="bg-[#7C5A76] text-white px-1.5 py-0.5 rounded font-semibold text-[7px]">New</span>
              <span className="font-semibold text-slate-700">Rental Contracts ⚙️</span>
              <div className="ml-2 flex items-center gap-1 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-[7px] text-slate-400">
                <span>⭐</span>
                <span className="bg-amber-100 text-amber-700 px-1 rounded text-[6.5px] font-semibold">Running contracts ×</span>
                <span>🔍 Search...</span>
              </div>
              <span className="text-slate-300">▾</span>
              <div className="ml-auto flex items-center gap-0.5 text-[7px] text-slate-400">
                <button className="border border-[#7C5A76] bg-[#f5f0f5] text-[#7C5A76] rounded p-0.5 font-bold">▣</button>
                <button className="border border-slate-200 rounded p-0.5">☰</button>
                <button className="border border-slate-200 rounded p-0.5">📅</button>
              </div>
            </div>

            {/* Kanban Columns */}
            <div className="grid grid-cols-3 gap-0 divide-x divide-slate-100 px-0">
              {kanbanCols.map((col, ci) => (
                <div key={ci} className="px-2 py-2 space-y-1.5">
                  {/* Column header */}
                  <div className="flex items-center justify-between text-[7.5px] font-bold text-slate-700 pb-0.5">
                    <span>{col.title}</span>
                    <span className="text-slate-400 font-normal">{col.count}</span>
                  </div>
                  {/* Progress bar */}
                  <div className="h-0.5 w-full bg-slate-100 rounded" />

                  {/* Tenant rows */}
                  {col.tenants.map((t, ti) => (
                    <div key={ti} className="bg-white border border-slate-100 rounded-lg p-1.5 shadow-sm space-y-0.5 text-[7px]">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-slate-800 truncate">{t.name}</span>
                        <span className="text-slate-700 font-bold ml-1 flex-shrink-0">{t.amount}</span>
                      </div>
                      <div className="flex items-center justify-between text-[6.5px] text-slate-400">
                        <div className="flex items-center gap-0.5">
                          <span>{t.so}</span>
                          <span className="text-slate-300">{t.date}</span>
                          <span>⏱</span>
                        </div>
                        <span className="bg-emerald-500 text-white px-1 py-0.2 rounded text-[6px] font-semibold flex-shrink-0">Sales Order</span>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

          </div>
        </motion.div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   2. PROPERTY MANAGEMENT: CONTRACTS THAT KEEP EVERYONE HAPPY
   Top smiley + split main section (Caveat title + Odoo Quotation form)
   + bottom 2-column feature text + testimonial pill callout
   ───────────────────────────────────────────────────────────── */
export function PropertyContractsKeepEveryoneHappySection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-6 sm:px-12 lg:px-24 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto space-y-12">

        {/* TOP MAIN SPLIT CONTAINER WITH LIGHT GLASS BACKGROUND CARD */}
        <div className="relative bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 p-6 sm:p-10 overflow-hidden">

          {/* Yellow Smiley Face Emoji at top-left corner */}
          <div className="absolute top-4 left-4 sm:top-6 sm:left-6 text-3xl sm:text-4xl select-none">
            😊
          </div>

          <div className="grid lg:grid-cols-12 gap-8 items-center pt-6 sm:pt-4">

            {/* LEFT: TITLE & SUBTEXT */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-5 space-y-5 text-left"
            >
              <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.1]`}>
                <span className="relative inline-block">
                  Contracts
                  {/* Yellow highlighter underline */}
                  <svg className="absolute -bottom-1 left-0 w-full h-4 overflow-visible" viewBox="0 0 160 12" fill="none">
                    <path d="M 2 6 Q 40 1, 80 6 T 158 6" stroke="#FBBF24" strokeWidth="6" strokeLinecap="round" fill="none" />
                  </svg>
                </span>{" "}
                that keep<br />
                everyone happy
              </h2>

              <p className="text-gray-300 text-xl leading-relaxed font-normal">
                <strong className="text-white font-bold">Simplified rental contract creation.</strong>{" "}
                Create rental contracts quickly by selecting a customer, choosing a property, and setting rental terms.
              </p>
            </motion.div>


            {/* RIGHT: ODOO QUOTATION / RENTAL CONTRACT FORM CARD */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="lg:col-span-7 w-full flex justify-center lg:justify-end"
            >
              <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 text-slate-800 font-sans overflow-hidden w-full max-w-[560px] text-[7.5px]">

                {/* Top Action & Status bar */}
                <div className="flex flex-wrap items-center justify-between border-b border-slate-100 px-3 py-1.5 gap-1">
                  <div className="flex items-center gap-1">
                    <button className="bg-[#7C5A76] text-white px-2 py-0.5 rounded font-semibold text-[7px]">Send by Email</button>
                    <button className="border border-slate-200 text-slate-600 px-2 py-0.5 rounded text-[7px]">Confirm</button>
                    <button className="border border-slate-200 text-slate-600 px-2 py-0.5 rounded text-[7px]">Preview</button>
                  </div>
                  <div className="flex items-center gap-1 text-[6.5px]">
                    <span className="border border-[#7C5A76] text-[#7C5A76] rounded px-1.5 py-0.5 font-semibold">Quotation</span>
                    <span className="text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">Quotation Sent</span>
                    <span className="text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">Sales Order</span>
                  </div>
                </div>

                {/* Form fields body */}
                <div className="p-3 space-y-3">
                  <div className="text-lg font-bold text-slate-900">New</div>

                  {/* Two column form fields */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[7.5px]">
                    <div className="space-y-1.5">
                      <div className="grid grid-cols-3 items-center">
                        <span className="text-slate-500 col-span-1">Customer</span>
                        <div className="col-span-2 flex items-center justify-between border-b border-slate-300 pb-0.5 font-semibold text-slate-800">
                          <span>Bertrand Dupuits</span>
                          <span className="text-slate-400 text-[6px]">▾ ↗</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 items-center">
                        <span className="text-slate-500 col-span-1">Quotation Template</span>
                        <div className="col-span-2 border-b border-slate-200 pb-0.5 text-right text-slate-400">▾</div>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <div className="grid grid-cols-3 items-center">
                        <span className="text-slate-500 col-span-1">Expiration <span className="text-slate-300">?</span></span>
                        <span className="col-span-2 text-slate-800 font-semibold">10/23/2024</span>
                      </div>
                      <div className="grid grid-cols-3 items-center">
                        <span className="text-slate-500 col-span-1">Recurring Plan</span>
                        <div className="col-span-2 border-b border-slate-200 pb-0.5 text-right text-slate-400">▾</div>
                      </div>
                      <div className="grid grid-cols-3 items-center">
                        <span className="text-slate-500 col-span-1">Payment Terms</span>
                        <span className="col-span-2 text-slate-800 font-semibold">30 Days</span>
                      </div>
                    </div>
                  </div>

                  {/* Lines Tabs & Table */}
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="flex items-center border-b border-slate-100 px-2 bg-slate-50 text-[7px]">
                      {["Order Lines", "Optional Products", "Other Info", "Notes"].map((t, i) => (
                        <button key={i} className={`px-2 py-1 font-medium ${i === 0 ? "border-b-2 border-[#7C5A76] text-[#7C5A76]" : "text-slate-400"}`}>{t}</button>
                      ))}
                    </div>

                    <div className="grid grid-cols-6 bg-slate-50 border-b border-slate-100 px-2 py-1 font-bold text-slate-500 text-[6.5px]">
                      <span className="col-span-2">Product</span>
                      <span className="text-right">Quantity</span>
                      <span className="text-right">Unit Price</span>
                      <span className="text-center">Taxes</span>
                      <span className="text-right">Amount</span>
                    </div>

                    {[
                      { item: "Rental fee", qty: "1.00", price: "35.00", tax: "15%", amt: "$ 35.00" },
                      { item: "Water", qty: "1.00", price: "3.00", tax: "15%", amt: "$ 3.00" },
                      { item: "Security deposit", qty: "1.00", price: "2,000.00", tax: "15%", amt: "$ 2,000.00" },
                    ].map((row, i) => (
                      <div key={i} className="grid grid-cols-6 px-2 py-1 border-b border-slate-50 items-center text-[7px]">
                        <span className="col-span-2 text-slate-700 font-medium flex items-center gap-1">
                          <span className="text-slate-300">:::</span> {row.item}
                        </span>
                        <span className="text-right text-slate-600">{row.qty}</span>
                        <span className="text-right text-slate-600">{row.price}</span>
                        <span className="text-center"><span className="bg-slate-100 text-slate-600 px-1 rounded text-[6px]">{row.tax}</span></span>
                        <span className="text-right text-[#7C5A76] font-bold">{row.amt}</span>
                      </div>
                    ))}

                    <div className="p-1.5 flex items-center gap-3 text-[6.5px] text-[#7C5A76]">
                      <span>Add a product</span>
                      <span>Add a section</span>
                      <span>Add a note</span>
                      <span className="text-slate-400 border-l border-slate-200 pl-3">Catalog</span>
                    </div>
                  </div>

                  {/* Terms & Totals */}
                  <div className="grid grid-cols-2 gap-4 items-end pt-1">
                    <div className="border border-slate-200 rounded p-1.5 text-slate-300 text-[6.5px] h-12">
                      Terms and conditions...
                    </div>
                    <div className="space-y-0.5 text-right text-[7px]">
                      <div className="flex justify-between text-slate-500">
                        <span>Untaxed Amount:</span>
                        <span className="font-semibold text-slate-800">$ 2,038.00</span>
                      </div>
                      <div className="flex justify-between text-slate-500">
                        <span>Tax 15%:</span>
                        <span className="font-semibold text-slate-800">$ 305.70</span>
                      </div>
                      <div className="flex justify-between text-slate-900 font-bold border-t border-slate-200 pt-0.5 text-[8px]">
                        <span>Total:</span>
                        <span>$ 2,343.70</span>
                      </div>
                    </div>
                  </div>

                </div>

              </div>
            </motion.div>

          </div>
        </div>

        {/* BOTTOM FEATURE GRID (2 COLUMNS) + TESTIMONIAL PILL */}
        <div className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-base sm:text-lg lg:text-xl text-slate-300 leading-relaxed max-w-5xl mx-auto font-normal">
            <p>
              <strong className="text-white font-bold">Automated invoicing and billing.</strong>{" "}
              Define billing dates and set up automatic invoices based on your contract terms.
            </p>
            <p>
              <strong className="text-white font-bold">Meter readings and move-in logs.</strong>{" "}
              Track utility usage with fields for meter readings at move-in, ensuring accurate records and transparency with tenants.
            </p>
          </div>


          {/* Testimonial Pill (Floating bottom right) */}
          <div className="flex justify-end pr-4 gap-2">
            
              <img
                src="/odoo/unsplash/avatars/u5.jpg"
                alt="Avatar"
                className="w-14 h-14 rounded-full object-cover border border-white/30"
              />
              <div className="bg-white/10 text-slate-800 rounded-full shadow-2xl p-2.5 sm:p-3 px-5 sm:px-8 border border-slate-100 text-center">
              <i className="text-white text-xs sm:text-base font-semibold block leading-snug">
                Always find the right place at the right time!
              </i>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   3. PROPERTY MANAGEMENT: AVAILABILITY AT A GLANCE
   Left: Gantt availability timeline card + blue spark doodle
   Right: Caveat title + dotted blue underline + 2 copy blocks
   ───────────────────────────────────────────────────────────── */
export function PropertyAvailabilityAtAGlanceSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">


          {/* LEFT: GANTT AVAILABILITY CARD + BLUE SPARK ACCENT (max-w-[580px]) */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 relative w-full flex justify-center lg:justify-start"
          >
            {/* Top-left Blue Spark Burst Doodle Accent */}
            <div className="absolute -top-6 -left-4 sm:-top-8 sm:-left-6 pointer-events-none select-none z-20">
              <svg className="w-12 h-12 text-sky-400" viewBox="0 0 50 50" fill="none">
                <path d="M 25 5 L 25 15" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                <path d="M 8 15 L 16 22" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                <path d="M 5 32 L 15 30" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                <path d="M 40 10 L 32 18" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </div>

            {/* Odoo Availability Gantt Card */}
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 text-slate-800 font-sans overflow-hidden w-full max-w-5xl text-[7.5px] relative">

              {/* Top Nav Bar */}
              <div className="flex flex-wrap items-center justify-between bg-white border-b border-slate-100 px-3 py-1.5 text-[7.5px] text-slate-500 gap-1">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 text-white text-[8px] font-bold flex-shrink-0">●</span>
                  <span className="font-semibold text-slate-700">Properties</span>
                  <span>Rental Contracts</span>
                  <span className="font-semibold text-slate-800 border-b-2 border-[#7C5A76]">Availability</span>
                  <span>Properties</span>
                  <span>Configuration</span>
                </div>
                <div className="flex items-center gap-1 text-[7px] text-slate-400">
                  <span>🔴</span><span>💬</span><span>⏱</span><span>✕</span>
                  <span className="font-semibold text-slate-700 ml-1">My Company</span>
                  <span className="inline-flex w-4 h-4 rounded-full bg-emerald-500 items-center justify-center text-white text-[7px] font-bold">A</span>
                </div>
              </div>

              {/* Sub-nav: New + Availability filter + zoom */}
              <div className="flex flex-wrap items-center justify-between border-b border-slate-100 px-3 py-1.5 text-[7.5px] gap-1">
                <div className="flex items-center gap-1.5">
                  <span className="bg-[#7C5A76] text-white px-1.5 py-0.5 rounded font-semibold text-[7px]">New</span>
                  <span className="font-semibold text-slate-700">Availability ⚙️</span>
                  <div className="ml-1 flex items-center gap-1 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-[6.5px] text-slate-400">
                    <span>⭐</span>
                    <span className="bg-amber-100 text-amber-800 px-1 rounded font-semibold">Availability ×</span>
                    <span>🔍 Search...</span>
                  </div>
                </div>

                <div className="flex items-center gap-1 text-[6.5px] text-slate-400">
                  <button className="border border-slate-200 rounded px-1">←</button>
                  <button className="border border-slate-200 rounded px-1">→</button>
                  <span>From : 06/01/2024 to 11/30/2024</span>
                  <span>🔍</span>
                  <input type="range" className="w-10 accent-slate-600 h-1" />
                  <button className="border border-[#7C5A76] bg-[#f5f0f5] text-[#7C5A76] rounded px-1 font-bold">📊</button>
                  <button className="border border-slate-200 rounded px-1">≡</button>
                </div>
              </div>

              {/* GANTT TIMELINE GRID */}
              <div className="relative overflow-x-auto">

                {/* Month Headers */}
                <div className="grid grid-cols-12 bg-slate-50 border-b border-slate-100 text-[6.5px] text-slate-500 font-semibold py-1">
                  <div className="col-span-3 px-2 border-r border-slate-200">Availability</div>
                  <div className="col-span-1 text-center border-r border-slate-100">2024</div>
                  <div className="col-span-1 text-center border-r border-slate-100">June</div>
                  <div className="col-span-1 text-center border-r border-slate-100">July</div>
                  <div className="col-span-2 text-center border-r border-slate-100">August</div>
                  <div className="col-span-2 text-center border-r border-slate-200 bg-amber-50 text-slate-900 font-bold">September</div>
                  <div className="col-span-2 text-center">November</div>
                </div>

                {/* Group 1: Park Station */}
                <div className="border-b border-slate-100 bg-slate-50/50 px-2 py-0.5 text-[6.5px] font-bold text-slate-700 flex justify-between">
                  <span>▼ Park Station</span>
                  <span className="text-slate-400">4</span>
                </div>

                {/* Office M */}
                <div className="grid grid-cols-12 border-b border-slate-50 text-[6.5px] items-center py-1 relative">
                  <div className="col-span-3 px-2 font-medium text-slate-600 border-r border-slate-100">Office M</div>
                  <div className="col-span-9 relative h-4">
                    {/* Gantt Bar 1 */}
                    <div className="absolute left-[10%] w-[55%] h-3 top-0.5 bg-amber-400 rounded text-white text-[6px] font-bold px-1 flex items-center shadow-sm">
                      S00008
                    </div>
                    {/* Gantt Bar 2 */}
                    <div className="absolute left-[66%] w-[28%] h-3 top-0.5 bg-amber-600 rounded text-white text-[6px] font-bold px-1 flex items-center shadow-sm">
                      S00009
                    </div>
                  </div>
                </div>

                {/* Office S */}
                <div className="grid grid-cols-12 border-b border-slate-50 text-[6.5px] items-center py-1 relative">
                  <div className="col-span-3 px-2 font-medium text-slate-600 border-r border-slate-100">Office S</div>
                  <div className="col-span-9 relative h-4">
                    <div className="absolute left-[52%] w-[45%] h-3 top-0.5 bg-amber-500 rounded text-white text-[6px] font-bold px-1 flex items-center shadow-sm">
                      S00010
                    </div>
                  </div>
                </div>

                {/* Office T */}
                <div className="grid grid-cols-12 border-b border-slate-100 text-[6.5px] items-center py-1 relative">
                  <div className="col-span-3 px-2 font-medium text-slate-600 border-r border-slate-100">Office T</div>
                  <div className="col-span-9 relative h-4">
                    <div className="absolute left-[10%] w-[65%] h-3 top-0.5 bg-rose-400 rounded text-white text-[6px] font-bold px-1 flex items-center shadow-sm">
                      S00011
                    </div>
                  </div>
                </div>

                {/* Group 2: Immeuble Bellevue */}
                <div className="border-b border-slate-100 bg-slate-50/50 px-2 py-0.5 text-[6.5px] font-bold text-slate-700 flex justify-between">
                  <span>▼ Immeuble Bellevue</span>
                  <span className="text-slate-400">2</span>
                </div>

                {/* L'oncle Observatoire Duplex */}
                <div className="grid grid-cols-12 border-b border-slate-100 text-[6.5px] items-center py-1 relative">
                  <div className="col-span-3 px-2 font-medium text-slate-600 border-r border-slate-100 truncate">L'oncle Observatoire Duplex</div>
                  <div className="col-span-9 relative h-6">
                    <div className="absolute left-[10%] w-[85%] h-2.5 top-0 bg-rose-400 rounded text-white text-[5.5px] font-bold px-1 flex items-center">
                      S00006
                    </div>
                    <div className="absolute left-[10%] w-[85%] h-2.5 top-3 bg-rose-500 rounded text-white text-[5.5px] font-bold px-1 flex items-center">
                      S00007
                    </div>
                  </div>
                </div>

                {/* Group 3: Ferme Saint-Jean */}
                <div className="border-b border-slate-100 bg-slate-50/50 px-2 py-0.5 text-[6.5px] font-bold text-slate-700 flex justify-between">
                  <span>▼ Ferme Saint-Jean</span>
                  <span className="text-slate-400">3</span>
                </div>

                {/* Apartment 25 */}
                <div className="grid grid-cols-12 border-b border-slate-50 text-[6.5px] items-center py-1 relative">
                  <div className="col-span-3 px-2 font-medium text-slate-600 border-r border-slate-100">Apartment 25</div>
                  <div className="col-span-9 relative h-4">
                    <div className="absolute left-[10%] w-[80%] h-3 top-0.5 bg-pink-400 rounded text-white text-[6px] font-bold px-1 flex items-center">
                      S00003
                    </div>
                  </div>
                </div>

                {/* Apartment 27 */}
                <div className="grid grid-cols-12 border-b border-slate-50 text-[6.5px] items-center py-1 relative">
                  <div className="col-span-3 px-2 font-medium text-slate-600 border-r border-slate-100">Apartment 27</div>
                  <div className="col-span-9 relative h-4">
                    <div className="absolute left-[10%] w-[65%] h-3 top-0.5 bg-indigo-400 rounded text-white text-[6px] font-bold px-1 flex items-center">
                      S00002
                    </div>
                  </div>
                </div>

                {/* Apartment 20 */}
                <div className="grid grid-cols-12 text-[6.5px] items-center py-1 relative">
                  <div className="col-span-3 px-2 font-medium text-slate-600 border-r border-slate-100">Apartment 20</div>
                  <div className="col-span-9 relative h-4">
                    <div className="absolute left-[10%] w-[65%] h-3 top-0.5 bg-teal-400 rounded text-white text-[6px] font-bold px-1 flex items-center">
                      S00001
                    </div>
                    <div className="absolute left-[78%] w-[20%] h-3 top-0.5 bg-purple-400 rounded text-white text-[6px] font-bold px-1 flex items-center">
                      S00005
                    </div>
                  </div>
                </div>

                {/* FLOATING TOOLTIP CARD OVER SEPTEMBER COLUMN */}
                <div className="absolute top-[28%] left-[55%] w-36 bg-slate-900 text-white rounded-lg p-2 shadow-2xl border border-slate-700 text-[6.5px] space-y-1 z-30">
                  <div className="flex items-center justify-between font-bold border-b border-slate-700 pb-0.5">
                    <span>S00008</span>
                    <span className="text-slate-400 text-[6px] cursor-pointer">✕</span>
                  </div>
                  <div className="text-slate-300 space-y-0.5">
                    <div>Name: <span className="text-white font-medium">S00008</span></div>
                    <div>Start: <span className="text-white font-medium">9/20/2024, 12:00 AM</span></div>
                    <div>Stop: <span className="text-white font-medium">9/20/2024, 12:00 AM</span></div>
                  </div>
                  <button className="bg-slate-700 hover:bg-slate-600 text-white text-[6px] font-semibold px-2 py-0.5 rounded mt-1">
                    Edit
                  </button>
                </div>

              </div>
            </div>
          </motion.div>

          {/* RIGHT: TITLE & COPYWRITING */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5 space-y-5 text-left"
          >
            {/* Caveat handwritten title with dotted blue underline stroke */}
            <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
              <span className="relative inline-block">
                Availability
                {/* Dotted blue underline stroke */}
                <svg className="absolute -bottom-1 left-0 w-full h-4 overflow-visible" viewBox="0 0 200 12" fill="none">
                  <path d="M 2 6 L 198 6" stroke="#38BDF8" strokeWidth="5" strokeDasharray="8 6" strokeLinecap="round" fill="none" />
                </svg>
              </span>{" "}
              at a glance
            </h2>

            <div className="space-y-4 text-slate-300 text-base sm:text-lg lg:text-xl leading-relaxed font-normal">
              <p>
                <strong className="text-white font-bold">Visualize your rental schedule.</strong>{" "}
                The Gantt view shows all contracts and vacancies at a glance, making it easy to manage property availability and plan ahead.
              </p>
              <p>
                <strong className="text-white font-bold">Quick and easy booking.</strong>{" "}
                Add new rental contracts directly from the availability view, streamlining the booking process.
              </p>
            </div>

          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   4. PROPERTY MANAGEMENT: STAY ON TOP OF PROPERTY MANAGEMENT
   Top crown icon + Caveat title with amber highlighted "property"
   + 3 feature columns with teal checkmark circles + Odoo CRM Kanban
   ───────────────────────────────────────────────────────────── */
export function PropertyStayOnTopSection({ section }) {
  const crmCols = [
    {
      title: "New",
      count: 0,
      cards: [
        { name: "NEWHOMESMATE", stars: 0, tag: null, avatar: "A" },
        { name: "NXT Capital", stars: 0, tag: null, avatar: "A" },
        { name: "Apartment 37", desc: "YourCompany, Mitchell Garmin", tag: "Informations", avatar: "C" },
        { name: "John Doe", stars: 0, tag: null, avatar: "A" },
      ],
    },
    {
      title: "Visit",
      count: 0,
      cards: [
        { name: "USDA", stars: 0, icon: "👥", avatar: "A" },
        { name: "JLL", stars: 0, icon: "📞", avatar: "A" },
        { name: "CBRE Group", stars: 0, icon: "⏱", avatar: "A" },
        { name: "I'm interested", desc: "Apartment 27\nAdministrator", tag: "Hot", avatar: "A" },
      ],
    },
    {
      title: "Application",
      count: 0,
      cards: [
        { name: "I am highly interested in this property", desc: "Apartment 26\nAdministrator", stars: 3, icon: "📞", avatar: "C" },
        { name: "Madison Realty Capital", stars: 2, icon: "⏱", avatar: "A" },
        { name: "JLL", stars: 1, avatar: "A" },
        { name: "CBRE", stars: 0, avatar: "A" },
      ],
    },
    {
      title: "Won",
      count: 0,
      cards: [
        { name: "CBRE", stars: 2, icon: "⏱", avatar: "A" },
        { name: "Pacaso", stars: 1, icon: "⏱", avatar: "A" },
        { name: "Entrata", stars: 1, icon: "⏱", avatar: "A" },
        { name: "Homeward", stars: 0, icon: "⏱", avatar: "A" },
      ],
    },
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-6 sm:px-12 lg:px-24 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto space-y-10">

        {/* TOP HEADER: Crown icon + Title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center space-y-4 max-w-4xl mx-auto"
        >
          {/* Crown SVG Icon */}
          <div className="flex justify-center select-none">
            <svg className="w-12 h-12 text-amber-400" viewBox="0 0 48 48" fill="none">
              <path d="M 8 36 L 4 16 L 16 24 L 24 8 L 32 24 L 44 16 L 40 36 Z" fill="#FBBF24" stroke="#D97706" strokeWidth="2.5" strokeLinejoin="round" />
              <circle cx="24" cy="6" r="2.5" fill="#FBBF24" />
              <circle cx="4" cy="14" r="2.5" fill="#FBBF24" />
              <circle cx="44" cy="14" r="2.5" fill="#FBBF24" />
            </svg>
          </div>

          {/* Caveat Handwritten Title with Amber Highlight Box behind "property" */}
          <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
            Stay on top of{" "}
            <span className="bg-amber-400 text-slate-900 px-3 py-0.5 rounded-xl inline-block mx-1 transform -rotate-1 shadow-md">
              property
            </span>{" "}
            management
          </h2>
        </motion.div>

        {/* 3 FEATURE COLUMNS WITH TEAL CHECKMARK CIRCLES */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 w-full mx-auto">

          {/* Feature 1 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="space-y-3"
          >
            <div className="w-9 h-9 rounded-full bg-teal-400 text-slate-950 flex items-center justify-center font-bold text-lg shadow-md">
              ✓
            </div>
            <p className="text-slate-300 text-base sm:text-lg lg:text-xl leading-relaxed font-normal">
              <strong className="text-white font-bold">Organized property management.</strong>{" "}
              Manage all property details in one place with the Properties app. Easily update pricing and property-specific information.
            </p>
          </motion.div>

          {/* Feature 2 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="space-y-3"
          >
            <div className="w-9 h-9 rounded-full bg-teal-400 text-slate-950 flex items-center justify-center font-bold text-lg shadow-md">
              ✓
            </div>
            <p className="text-slate-300 text-base sm:text-lg lg:text-xl leading-relaxed font-normal">
              <strong className="text-white font-bold">Recurring product setup.</strong>{" "}
              Automate billing with recurring products and time-based pricing, adjusting rates and rental periods effortlessly.
            </p>
          </motion.div>

          {/* Feature 3 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-3"
          >
            <div className="w-9 h-9 rounded-full bg-teal-400 text-slate-950 flex items-center justify-center font-bold text-lg shadow-md">
              ✓
            </div>
            <p className="text-slate-300 text-base sm:text-lg lg:text-xl leading-relaxed font-normal">
              <strong className="text-white font-bold">Never miss a task.</strong>{" "}
              Track maintenance schedules and inspections with task management, keeping your properties in top shape.
            </p>
          </motion.div>


        </div>

        {/* BOTTOM MOCKUP: ODOO CRM PIPELINE KANBAN (Small & Precise max-w-[700px] centered) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="w-full flex justify-center pt-4"
        >
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 text-slate-800 font-sans overflow-hidden w-full max-w-5xl text-[7.5px]">

            {/* Top Nav Bar */}
            <div className="flex flex-wrap items-center justify-between bg-white border-b border-slate-100 px-3 py-1.5 text-[7.5px] text-slate-500 gap-1">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 text-white text-[8px] font-bold flex-shrink-0">●</span>
                <span className="font-semibold text-slate-700">CRM</span>
                <span>Sales</span>
                <span>Reporting</span>
                <span>Configuration</span>
              </div>
              <div className="flex items-center gap-1 text-[7px] text-slate-400">
                <span>💬</span><span>⏱</span><span>✕</span>
                <span className="font-semibold text-slate-700 ml-1">My Company</span>
                <span className="inline-flex w-4 h-4 rounded-full bg-emerald-500 items-center justify-center text-white text-[7px] font-bold">A</span>
              </div>
            </div>

            {/* Sub-nav: New + Generate Leads + search */}
            <div className="flex flex-wrap items-center justify-between border-b border-slate-100 px-3 py-1.5 text-[7.5px] gap-1">
              <div className="flex items-center gap-1.5">
                <span className="bg-[#7C5A76] text-white px-1.5 py-0.5 rounded font-semibold text-[7px]">New</span>
                <span className="border border-slate-200 text-slate-600 px-1.5 py-0.5 rounded text-[7px]">Generate Leads</span>
                <span className="font-semibold text-slate-700">Pipeline ⚙️</span>
              </div>
              <div className="flex items-center gap-1 text-[6.5px] text-slate-400">
                <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                  <span>🔍 Search...</span>
                </div>
                <button className="border border-[#7C5A76] bg-[#f5f0f5] text-[#7C5A76] rounded px-1 font-bold">▣</button>
                <button className="border border-slate-200 rounded px-1">☰</button>
                <button className="border border-slate-200 rounded px-1">📅</button>
                <button className="border border-slate-200 rounded px-1">📊</button>
                <button className="border border-slate-200 rounded px-1">🗺</button>
              </div>
            </div>

            {/* CRM Kanban Columns Grid */}
            <div className="grid grid-cols-4 divide-x divide-slate-100 bg-slate-50/30">
              {crmCols.map((col, ci) => (
                <div key={ci} className="p-2 space-y-1.5">
                  {/* Column Header */}
                  <div className="flex items-center justify-between font-bold text-slate-700 text-[7.5px] pb-0.5">
                    <span className="flex items-center gap-1">
                      {col.title} <span className="text-slate-400 font-normal text-[6.5px]">+</span>
                    </span>
                    <span className="text-slate-400 font-normal">{col.count}</span>
                  </div>
                  <div className="h-0.5 w-full bg-slate-200 rounded" />

                  {/* Cards */}
                  {col.cards.map((card, cdi) => (
                    <div key={cdi} className="bg-white border border-slate-100 rounded-lg p-1.5 shadow-sm space-y-1 text-[7px]">
                      <div className="font-semibold text-slate-800 truncate">{card.name}</div>
                      {card.desc && (
                        <div className="text-[6px] text-slate-400 leading-tight whitespace-pre-line">{card.desc}</div>
                      )}
                      {card.tag && (
                        <span className="bg-teal-100 text-teal-700 px-1 py-0.2 rounded text-[5.5px] font-semibold inline-block">
                          {card.tag}
                        </span>
                      )}
                      <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[6.5px]">
                        <div className="flex items-center gap-0.5 text-amber-400">
                          {card.stars > 0 ? "★".repeat(card.stars) + "☆".repeat(3 - card.stars) : "☆☆☆"}
                          {card.icon && <span className="text-slate-400 ml-1">{card.icon}</span>}
                        </div>
                        <span className="w-3.5 h-3.5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[6px] font-bold">
                          {card.avatar}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

          </div>
        </motion.div>

      </div>
    </section>
  );
}
